import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, Form, Input } from '@arco-design/web-react';

import {
  formJsonFieldFormatter,
  formJsonFieldNormalize,
  toFormJsonString,
} from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';

import { buildPresetListKeySet, resolveNamedListItemKey } from '../../platform/access-policy/orderListItemLock';
import { isFieldReadonly } from '../../platform/capability-registry/fieldControl';
import { isActionVisible, isFieldVisible } from '../../platform/capability-registry/resolveFieldMode';
import { useOptionalResourceFormContext } from '../../platform/resource-form';
import { ServicePortListEditor, createServiceListValidator } from './ServicePortListEditor';
import { APP_INSTANCE_USAGE_SCENARIO_OPTIONS, MODEL_INSTANCE_USAGE_SCENARIO_OPTIONS } from './constants';
import { SERVICE_ACTION_KEYS, SERVICE_FIELD_KEYS } from './fieldKeys';
import styles from './index.module.less';
import { resolveServicePaths } from './resolvePaths';
import type { ServiceBlockProps, ServiceFormItem, ServicePaths } from './types';
import { normalizeServiceList } from './utils';

const FormItem = Form.Item;

export const FRAMED_SERVICE_SECTION_OPTIONS = {
  sectionLayout: 'framed',
} as const;

function isJsonStringFormat(options: ServiceBlockProps['options']): boolean {
  return options?.valueFormat === 'json-string';
}

function writeServiceList(form: ServiceBlockProps['form'], path: string, list: ServiceFormItem[], jsonString: boolean) {
  form.setFieldValue(path, jsonString ? toFormJsonString(list) : list);
}

/** 服务配置能力块（数组 path 或 JSON 字符串；UI 共用 ServicePortListEditor） */
export function ServiceBlock(props: ServiceBlockProps) {
  const { form, paths: rawPaths, options, ...control } = props;
  const jsonString = isJsonStringFormat(options);
  const paths = useMemo((): ServicePaths => {
    if (rawPaths?.services) return rawPaths as ServicePaths;
    return resolveServicePaths('', options);
  }, [options, rawPaths]);
  const { t } = useTranslation();

  const requireAtLeastOne = options?.requireAtLeastOne !== false;
  const maxCount = options?.maxCount;
  /** preview / 显式 readonly：禁增删改；下单 orderListItemLock 时 list 保持 editable，可增，预置行由 isItemLocked 禁删改 */
  const listReadonly = isFieldReadonly(SERVICE_FIELD_KEYS.list, control);
  const canAdd = isActionVisible(SERVICE_ACTION_KEYS.add, control) && !listReadonly;
  const canEdit = isActionVisible(SERVICE_ACTION_KEYS.edit, control) && !listReadonly;
  const canRemove = isActionVisible(SERVICE_ACTION_KEYS.remove, control) && !listReadonly;
  const usageScenarioOptions =
    options?.usageScenarioOptions ?? (jsonString ? MODEL_INSTANCE_USAGE_SCENARIO_OPTIONS : APP_INSTANCE_USAGE_SCENARIO_OPTIONS);

  const servicesRaw = Form.useWatch(paths.services, form);
  /** normalizeServiceList 已兼容数组与 JSON 字符串 */
  const serviceList = useMemo(() => normalizeServiceList(servicesRaw), [servicesRaw]);
  const resourceFormCtx = useOptionalResourceFormContext();
  const skuPresetKeys = useMemo(
    () => buildPresetListKeySet(resourceFormCtx?.presetConfigSnapshot, paths.services, (row) => resolveNamedListItemKey(row, 'name')),
    [paths.services, resourceFormCtx?.presetConfigSnapshot],
  );
  /** 仅按 SKU 预置 name 锁行；镜像 `_isDefault` 仍走弹窗内字段级限制 */
  const isItemLocked = useCallback(
    (item: ServiceFormItem) => {
      const name = String(item?.name ?? '').trim();
      return Boolean(name && skuPresetKeys.has(name));
    },
    [skuPresetKeys],
  );

  const serviceListRules = useMemo(() => [createServiceListValidator(requireAtLeastOne, t, maxCount)], [maxCount, requireAtLeastOne, t]);

  const handleAdd = useCallback(
    (item: ServiceFormItem) => {
      // IaaS 数组形态补 externalPort；JSON 字符串形态保持弹窗原值（与历史 MaasServiceBlock 一致，避免提交多出 externalPort:0）
      const row = jsonString ? item : { ...item, externalPort: item.externalPort ?? 0 };
      writeServiceList(form, paths.services, [...normalizeServiceList(form.getFieldValue(paths.services)), row], jsonString);
    },
    [form, jsonString, paths.services],
  );

  const handleUpdate = useCallback(
    (index: number, item: ServiceFormItem) => {
      writeServiceList(
        form,
        paths.services,
        normalizeServiceList(form.getFieldValue(paths.services)).map((row, i) => (i === index ? item : row)),
        jsonString,
      );
    },
    [form, jsonString, paths.services],
  );

  const handleRemove = useCallback(
    (index: number) => {
      writeServiceList(
        form,
        paths.services,
        normalizeServiceList(form.getFieldValue(paths.services)).filter((_, i) => i !== index),
        jsonString,
      );
    },
    [form, jsonString, paths.services],
  );

  if (!isFieldVisible(SERVICE_FIELD_KEYS.list, control)) {
    return null;
  }

  const content = (
    <>
      <FormItem
        field={paths.services}
        hidden
        style={{ display: 'none', margin: 0 }}
        rules={serviceListRules}
        {...(jsonString
          ? {
              triggerPropName: 'value' as const,
              formatter: formJsonFieldFormatter,
              normalize: formJsonFieldNormalize,
            }
          : {})}
      >
        <Input />
      </FormItem>

      <ServicePortListEditor
        serviceList={serviceList}
        usageScenarioOptions={usageScenarioOptions}
        requireAtLeastOne={requireAtLeastOne}
        maxCount={maxCount}
        canAdd={canAdd}
        canEdit={canEdit}
        canRemove={canRemove}
        isItemLocked={isItemLocked}
        onAdd={handleAdd}
        onUpdate={handleUpdate}
        onRemove={handleRemove}
      />
    </>
  );

  const wrapped = jsonString ? <div className={styles.jsonServiceBlock}>{content}</div> : content;

  if (options?.sectionLayout === 'framed') {
    return (
      <Card bordered className={styles.serviceDashedCard}>
        {wrapped}
      </Card>
    );
  }

  return wrapped;
}
